import { Suspense } from "react";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";
import { QueryErrorResetBoundary } from "react-query";
import { ErrorBoundary } from "react-error-boundary";
import { LoginGuard, PrivateGuard } from "marvin-auth-kit";
import { ConfirmProvider } from "material-ui-confirm";
import { useTranslation } from "react-i18next";
import "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";

import { RootLayout } from "./layouts/RootLayout";
import { PageLoader } from "./components/page/loading/PageLoader";
import { LoginPage } from "./pages/LoginPage";
import { PasswordForgotPage } from "./pages/PasswordForgotPage";
import { CasesPage } from "./pages/CasesPage";
import { PasswordResetConfirmPage } from "./pages/PasswordResetConfirmPage";
import { CasePage } from "./pages/CasePage";
import { BackgroundConcentrationsPage } from "./pages/BackgroundConcentrationsPage";
import { MeteoPage } from "./pages/MeteoPage";
import { CaseCreatePage } from "./pages/CaseCreatePage";
import { CaseResultsPage } from "./pages/CaseResultsPage";
import { RootErrorFallback } from "./components/error-boundaries/RootErrorBoundary";
import { UsersPage } from "./pages/UsersPage";
import { UserPage } from "./pages/UserPage";
import { LoginRedirectPage } from "./pages/LoginRedirectPage";
import { AppLayout } from "./layouts/AppLayout";
import { LoginLinkPage } from "./pages/auth/LoginLinkPage";
import { useLanguages } from "./hooks/useLanguages";

interface Props {}

export function App(props: Props) {
  const { t } = useTranslation();
  const { data: languages } = useLanguages();

  if (!languages) return <PageLoader />;
  return (
    <ConfirmProvider
      defaultOptions={{
        confirmationText: t("label.ok"),
        cancellationText: t("label.cancel"),
      }}
    >
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary FallbackComponent={RootErrorFallback} onReset={reset}>
            <RootLayout>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/login" element={<LoginGuard redirectTo="/login-redirect" loader={<PageLoader />} />}>
                    <Route index element={<LoginPage />} />
                  </Route>
                  <Route path="/" element={<PrivateGuard redirectTo="/login" loader={<PageLoader />} />}>
                    <Route path="*" element={<AppLayout />}>
                      <Route path="cases" element={<Outlet />}>
                        <Route index element={<CasesPage />} />
                      </Route>
                      <Route path="custom-meteo" element={<MeteoPage />} />
                      <Route path="background-concentrations" element={<BackgroundConcentrationsPage />} />
                      <Route path="users">
                        <Route index element={<UsersPage />} />
                        <Route path=":userId" element={<UserPage />} />
                      </Route>
                    </Route>
                    <Route path="cases" element={<Outlet />}>
                      <Route path="new" element={<CaseCreatePage />} />
                      <Route path=":caseId" element={<Outlet />}>
                        <Route index element={<CasePage />} />
                        <Route path="results" element={<CaseResultsPage />} />
                        <Route path="*" element={<CasePage />} />
                      </Route>
                    </Route>

                    <Route path="login-redirect" element={<LoginRedirectPage />} />
                    <Route index element={<Navigate to="login-redirect" />} />
                  </Route>

                  <Route path="/password">
                    <Route path="forgot" element={<PasswordForgotPage />} />
                    <Route path="reset">
                      <Route index element={<PasswordResetConfirmPage />} />
                      <Route path=":token" element={<PasswordResetConfirmPage />} />
                    </Route>
                  </Route>
                  <Route path={"/loginlink"} element={<LoginLinkPage />} />
                  <Route path="*" element={<Navigate to="/login" />} />
                </Routes>
              </Suspense>
            </RootLayout>
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </ConfirmProvider>
  );
}
